import React, { useState, useCallback, useRef, memo, useEffect } from 'react';
import { useConversation } from '@11labs/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, X, RefreshCw, Mail } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { Button, Card } from './components/UIComponents';

const SCHOOLJOY_API_URL = 'https://schooljoy-staging.eastus2.cloudapp.azure.com';

const scenarios = [
  {
    id: 1,
    title: "![conversation](https://blog.coursify.me/wp-content/uploads/2018/08/plan-your-online-course.jpg) Math Tutor",
    subtitle: "Interactive Math Learning Session",
    description: "Get personalized help with mathematical concepts, problem-solving techniques, and homework assistance from an AI math tutor.",
    agentId: "uxex9Dxx88pfzByUGGc3",
    promptName: "generate_call_analysis_math_tutor"
  },
  {
    id: 2,
    title: "![conversation](https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=500) Language Coach",
    subtitle: "English Conversation Practice",
    description: "Practice your English speaking skills in a supportive environment with pronunciation feedback and grammar correction.",
    agentId: "uxex9Dxx88pfzByUGGc3",
    promptName: "generate_call_analysis_language_coach"
  },
  {
    id: 3,
    title: "![conversation](https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=500) Career Counselor",
    subtitle: "Professional Development Guidance",
    description: "Receive career advice, interview preparation tips, and professional development guidance tailored to your goals.",
    agentId: "uxex9Dxx88pfzByUGGc3",
    promptName: "generate_call_analysis_career_counselor"
  },
  {
    id: 4,
    title: "![conversation](https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=500) Wellness Coach",
    subtitle: "Mental Health & Wellness Support",
    description: "Engage in supportive conversations about stress management, mindfulness, and overall well-being strategies.",
    agentId: "uxex9Dxx88pfzByUGGc3",
    promptName: "generate_call_analysis_wellness_coach"
  },
  {
    id: 5,
    title: "![conversation](https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=500) Technical Mentor",
    subtitle: "Programming & Tech Support",
    description: "Get help with coding problems, technical concepts, and programming best practices from an experienced AI mentor.",
    agentId: "uxex9Dxx88pfzByUGGc3",
    promptName: "generate_call_analysis_technical_mentor"
  }
];

const processContent = (text) => {
  const imageRegex = /^!\[.*?\]\((.*?)\)/;
  const match = text.match(imageRegex);
  
  if (match) {
    const imageUrl = match[1];
    const remainingText = text.replace(imageRegex, '').trim();
    return {
      imageUrl,
      text: remainingText
    };
  }
  return {
    imageUrl: null,
    text
  };
};

const ScenarioCard = ({ title, subtitle, description, onLaunch }) => {
  const processedTitle = processContent(title);

  return (
    <Card className="group transform transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 rounded-2xl overflow-hidden border-0 h-full flex flex-col relative bg-white/70 backdrop-blur-sm hover:bg-white/90 shadow-lg hover:shadow-blue-100/50">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="h-full flex flex-col relative"
      >
        {/* Gradient border overlay */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary-blue/20 via-transparent to-secondary-blue-1/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
        
        {/* Image Section */}
        {processedTitle.imageUrl && (
          <div className="relative overflow-hidden rounded-t-2xl">
            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent z-10"></div>
            <img 
              src={processedTitle.imageUrl} 
              alt={`Illustration for ${processedTitle.text}`}
              className="w-full h-52 md:h-60 object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        )}

        {/* Content Section */}
        <div className="p-8 bg-gradient-to-br from-white via-white to-blue-50/30 flex-grow flex flex-col relative z-10">
          <h3 className="text-2xl md:text-3xl font-poppins font-bold mb-4 text-primary-blue leading-tight group-hover:text-secondary-blue-1 transition-colors duration-300">
            {processedTitle.text}
          </h3>

          <h4 className="text-xl font-poppins font-semibold text-secondary-blue-1 mb-6 group-hover:text-primary-blue transition-colors duration-300 flex-grow">
            {subtitle}
          </h4>
          
          <Button 
            onClick={onLaunch} 
            variant="primary" 
            className="w-full mt-auto group-hover:shadow-lg group-hover:shadow-primary-blue/25 transition-all duration-300 transform group-hover:scale-105"
          >
            <Phone size={20} className="mr-3 transition-transform duration-300 group-hover:rotate-12" />
            Start Demo
          </Button>
        </div>
      </motion.div>
    </Card>
  );
};

const ActiveCall = memo(({ scenarioTitle, status, onEndCall, subtitle, description, transcript }) => {
  const processedTitle = processContent(scenarioTitle);
  
  const parseTranscriptMessages = (transcriptText) => {
    if (!transcriptText) return [];
    
    const lines = transcriptText.split('\n');
    const messages = [];
    
    lines.forEach(line => {
      const trimmedLine = line.trim();
      if (trimmedLine) {
        if (trimmedLine.toLowerCase().startsWith('[assistant]')) {
          messages.push({
            type: 'agent',
            text: trimmedLine.substring(11).trim()
          });
        } else if (trimmedLine.toLowerCase().startsWith('[user]')) {
          messages.push({
            type: 'user',
            text: trimmedLine.substring(6).trim()
          });
        } else if (trimmedLine.includes('[Assistant]')) {
          messages.push({
            type: 'agent',
            text: trimmedLine.replace(/\[Assistant\]:\s*/, '').trim()
          });
        } else if (trimmedLine.includes('[User]')) {
          messages.push({
            type: 'user',
            text: trimmedLine.replace(/\[User\]:\s*/, '').trim()
          });
        } else {
          // If no clear speaker, treat as agent message
          messages.push({
            type: 'agent',
            text: trimmedLine
          });
        }
      }
    });
    
    return messages;
  };

  const messages = parseTranscriptMessages(transcript);

  return (
    <div className="w-full max-w-7xl">
      {/* Header Section - Mobile and Desktop */}
      <Card className="p-4 sm:p-6 mb-4 bg-white shadow-lg rounded-xl border border-gray-100">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          {processedTitle.imageUrl && (
            <img 
              src={processedTitle.imageUrl} 
              alt="" 
              className="w-full h-32 sm:h-40 object-cover rounded-lg mb-4 mx-auto" 
            />
          )}
          <h2 className="text-xl sm:text-2xl font-poppins font-bold mb-2 text-primary-blue">
            {processedTitle.text}
          </h2>
          <h3 className="text-lg font-poppins font-medium text-secondary-blue-1 mb-2">
            {subtitle}
          </h3>
          
          {/* Call Status */}
          <div className="flex items-center justify-center mb-4">
            <motion.div 
              className="w-12 h-12 bg-gradient-to-br from-secondary-blue-1 to-secondary-blue-2 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white mr-3"
              animate={{ 
                scale: [1, 1.05, 1],
                opacity: [1, 0.8, 1]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <Phone size={20} className="text-text-light" />
            </motion.div>
            <p className="text-secondary-blue-1 text-sm font-medium">
              {status}
            </p>
          </div>

          <Button 
            onClick={onEndCall} 
            variant="danger" 
            className="px-6 py-2 text-sm"
          >
            <X size={16} className="mr-2" />
            End Demo
          </Button>
        </motion.div>
      </Card>

      {/* Live Conversation Section - Side by Side on Desktop */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="flex flex-col lg:flex-row gap-4"
      >
        {/* Live Transcript - WhatsApp Style */}
        <Card className="flex-1 bg-gray-50 p-4">
          <h3 className="text-lg font-poppins font-semibold text-primary-blue mb-4">Live Conversation</h3>
          <div 
            className="h-[60vh] lg:h-[70vh] overflow-y-auto space-y-3 bg-white rounded-lg p-4"
            ref={el => {
              if (el && el.scrollHeight - el.scrollTop <= el.clientHeight + 100) {
                requestAnimationFrame(() => {
                  el.scrollTop = el.scrollHeight;
                });
              }
            }}
          >
            {messages.length > 0 ? (
              messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[75%] px-4 py-2 rounded-2xl shadow-sm ${
                      message.type === 'user'
                        ? 'bg-primary-blue text-white rounded-br-md'
                        : 'bg-white text-gray-800 rounded-bl-md border border-gray-200'
                    }`}
                  >
                    <p className="text-sm leading-relaxed break-words">
                      {message.text}
                    </p>
                    <div className={`text-xs mt-1 ${
                      message.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      {message.type === 'user' ? 'You' : 'AI Assistant'}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex justify-center items-center h-full">
                <p className="text-secondary-blue-1 italic text-sm">Start speaking to see the conversation...</p>
              </div>
            )}
          </div>
        </Card>

        {/* Live Analysis/Tips Section */}
        <Card className="flex-1 lg:max-w-md bg-white p-4">
          <h3 className="text-lg font-poppins font-semibold text-primary-blue mb-4">Conversation Tips</h3>
          <div className="h-[60vh] lg:h-[70vh] overflow-y-auto space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <h4 className="font-semibold text-blue-800 mb-2">💡 Tips for Better Conversation</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Speak clearly and at a moderate pace</li>
                <li>• Ask follow-up questions to dive deeper</li>
                <li>• Don't hesitate to ask for clarification</li>
                <li>• Take your time to think before responding</li>
              </ul>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg border border-green-100">
              <h4 className="font-semibold text-green-800 mb-2">🎯 Session Goals</h4>
              <p className="text-sm text-green-700">
                {description}
              </p>
            </div>

            {messages.length > 3 && (
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                <h4 className="font-semibold text-purple-800 mb-2">📈 Progress Update</h4>
                <p className="text-sm text-purple-700">
                  Great conversation so far! You've exchanged {messages.length} messages. 
                  Keep engaging to get the most out of this session.
                </p>
              </div>
            )}
          </div>
        </Card>
      </motion.div>
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.scenarioTitle === nextProps.scenarioTitle &&
         prevProps.status === nextProps.status &&
         prevProps.subtitle === nextProps.subtitle &&
         prevProps.description === nextProps.description &&
         prevProps.transcript === nextProps.transcript;
});

const TranscriptSection = memo(({ transcript }) => {
  const parseTranscriptMessages = (transcriptText) => {
    if (!transcriptText) return [];
    
    const lines = transcriptText.split('\n');
    const messages = [];
    
    lines.forEach(line => {
      const trimmedLine = line.trim();
      if (trimmedLine) {
        if (trimmedLine.toLowerCase().startsWith('[assistant]')) {
          messages.push({
            type: 'agent',
            text: trimmedLine.substring(11).trim()
          });
        } else if (trimmedLine.toLowerCase().startsWith('[user]')) {
          messages.push({
            type: 'user',
            text: trimmedLine.substring(6).trim()
          });
        } else if (trimmedLine.includes('[Assistant]')) {
          messages.push({
            type: 'agent',
            text: trimmedLine.replace(/\[Assistant\]:\s*/, '').trim()
          });
        } else if (trimmedLine.includes('[User]')) {
          messages.push({
            type: 'user',
            text: trimmedLine.replace(/\[User\]:\s*/, '').trim()
          });
        } else {
          // If no clear speaker, treat as agent message
          messages.push({
            type: 'agent',
            text: trimmedLine
          });
        }
      }
    });
    
    return messages;
  };

  const messages = parseTranscriptMessages(transcript);

  return (
    <motion.div 
      className="w-full sm:w-1/2 mb-4 sm:mb-0 sm:pr-2"
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h3 className="text-lg sm:text-h3 font-poppins font-semibold text-primary-blue mb-4">Conversation</h3>
      <Card className="h-[50vh] sm:h-[70vh] overflow-y-auto bg-gray-50">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="p-4 space-y-3"
        >
          {messages.length > 0 ? (
            messages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.3 }}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[75%] px-4 py-2 rounded-2xl shadow-sm ${
                    message.type === 'user'
                      ? 'bg-primary-blue text-white rounded-br-md'
                      : 'bg-white text-gray-800 rounded-bl-md border border-gray-200'
                  }`}
                >
                  <p className="text-sm leading-relaxed break-words">
                    {message.text}
                  </p>
                  <div className={`text-xs mt-1 ${
                    message.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                  }`}>
                    {message.type === 'user' ? 'You' : 'AI Assistant'}
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="flex justify-center items-center h-full">
              <p className="text-secondary-blue-1 italic">Conversation not available. Please try again.</p>
            </div>
          )}
        </motion.div>
      </Card>
    </motion.div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if the transcript actually changed
  return prevProps.transcript === nextProps.transcript;
});

const AnalysisSection = memo(({ analysis }) => {
  return (
    <motion.div 
      className="w-full sm:w-1/2 sm:pl-2"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h3 className="text-lg sm:text-h3 font-poppins font-semibold text-primary-blue mb-4">Call Analysis</h3>
      <Card className="h-[50vh] sm:h-[70vh] overflow-y-auto bg-white">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="p-4 sm:p-6"
        >
          <ReactMarkdown 
            rehypePlugins={[rehypeRaw]} 
            remarkPlugins={[remarkGfm]}
            className="text-text-dark"
          >
            {analysis || 'Analysis will be generated after the call ends.'}
          </ReactMarkdown>
        </motion.div>
      </Card>
    </motion.div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if the analysis actually changed
  return prevProps.analysis === nextProps.analysis;
});

const EmailForm = ({ onSendEmail, onCancel }) => {
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onCancel]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);
    try {
      await onSendEmail(email);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      style={{ pointerEvents: 'auto' }}
    >
      <motion.div 
        ref={formRef}
        className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full"
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-poppins font-semibold text-primary-blue leading-tighter tracking-tight">Send Results via Email</h3>
          <button 
            onClick={onCancel}
            className="text-placeholder hover:text-muted transition-colors"
            type="button"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-inter font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all duration-300"
              required
              disabled={isSending}
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all duration-300 font-medium disabled:opacity-50"
              disabled={isSending}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSending}
              className="px-6 py-2.5 bg-primary-blue text-white rounded-lg hover:bg-primary-blue-dark transition-all duration-300 font-medium flex items-center justify-center min-w-[100px] disabled:opacity-50"
            >
              {isSending ? (
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <RefreshCw size={20} className="mr-2" />
                </motion.span>
              ) : (
                <Mail size={20} className="mr-2" />
              )}
              {isSending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

const ElevenLabsDemo = () => {
  const [activeScenario, setActiveScenario] = useState(null);
  const [activeSubtitle, setActiveSubtitle] = useState(null);
  const [activeDescription, setActiveDescription] = useState(null);
  const [status, setStatus] = useState('Ready to start');
  const [currentView, setCurrentView] = useState('selection');
  const [transcript, setTranscript] = useState('');
  const [analysis, setAnalysis] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  // Refs for batching and performance
  const pendingTranscriptUpdates = useRef([]);
  const lastUpdateTime = useRef(Date.now());
  const updateDebounceTime = useRef(500);
  
  const updateTranscriptDebounced = useCallback((newText) => {
    pendingTranscriptUpdates.current.push(newText);
  }, []);

  // Handle batched transcript updates with cleanup
  useEffect(() => {
    const updateInterval = setInterval(() => {
      const now = Date.now();
      // Only update if enough time has passed and we have updates
      if (pendingTranscriptUpdates.current.length > 0 && 
          now - lastUpdateTime.current >= updateDebounceTime.current) {
        const updates = pendingTranscriptUpdates.current;
        pendingTranscriptUpdates.current = [];
          // Update transcript with all pending messages
          setTranscript(prev => {
            lastUpdateTime.current = now;
            // Dynamically adjust update frequency based on volume
            updateDebounceTime.current = updates.length > 5 ? 800 : 500;
            return prev + updates.join('\n') + '\n';
          });
      }
    }, 500); // Increased batching interval for better audio performance

    return () => {
      clearInterval(updateInterval);
      // Flush any remaining updates
      if (pendingTranscriptUpdates.current.length > 0) {
        const updates = pendingTranscriptUpdates.current;
        pendingTranscriptUpdates.current = [];
        setTranscript(prev => prev + updates.join('\n') + '\n');
      }
    };
  }, []);

  const conversation = useConversation({
    onConnect: () => setStatus('Connected'),
    onDisconnect: () => {
      console.log('Call disconnected');
      setStatus('Disconnected');
      // Analysis will be handled by endCall
    },
    onMessage: (message) => {
      console.log('Message received:', message);
      
      // Handle user transcripts - try both nested and direct formats
      if (message && message.type === 'user_transcript') {
        let transcriptText = null;
        
        // Try nested format
        if (message.user_transcription_event && message.user_transcription_event.user_transcript) {
          transcriptText = message.user_transcription_event.user_transcript;
        }
        // Try direct format
        else if (message.text || message.user_transcript) {
          transcriptText = message.text || message.user_transcript;
        }

        if (transcriptText) {
          console.log('User transcript:', transcriptText);
          updateTranscriptDebounced(`[User]: ${transcriptText}`);
        }
        return;
      }

      // Prioritize agent responses by handling them first
      if (message && (message.type === 'agent_response' || message.type === 'agent_message' || message.source === 'ai')) {
        let responseText = message.agent_response_event?.agent_response   // Nested format
                       || message.text                                    // Direct format
                       || message.message                                // Alternative format
                       || message.agent_response                         // Legacy format
                       || (message.source === 'ai' && message.message);  // Source format
        
        if (responseText) {
          console.log('Assistant response:', responseText);
          updateTranscriptDebounced(`[Assistant]: ${responseText}`);
        }
        return;
      }

      // Handle legacy source/message format
      if (message && message.source && message.message) {
        const role = message.source === 'ai' ? 'Assistant' : 'User';
        const messageText = `[${role}]: ${message.message}\n`;
        updateTranscriptDebounced(messageText.trim());
        return;
      }

      // Log unhandled message types for debugging (excluding common non-transcript types)
      if (message && message.type && 
          !['user_audio_chunk', 'vad_score_event', 'start_speaking', 'stop_speaking'].includes(message.type)) {
        console.log('Unhandled message type:', message.type, message);
      }
    },
    onError: (error) => setStatus(`Error: ${error.message}`),
    onStatusChange: (status) => setStatus(status),
    onModeChange: (mode) => {
      // Only update status if it's actually changed
      setStatus(prev => {
        const newStatus = mode === 'speaking' ? 'AI is speaking' : 'AI is listening';
        return prev !== newStatus ? newStatus : prev;
      });
    }
  });

  const startCall = async (scenarioTitle, agentId, subtitle, description) => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      
      await conversation.startSession({
        agentId: agentId,
        options: {
          audioConfig: {
            enableFastResponse: true,
            chunkSize: 2048,  // Smaller chunks for faster processing
            vadOptions: {
              silenceThreshold: 400  // Reduced silence threshold
            }
          }
        }
      });

      const id = conversation.getId();
      console.log('New conversation started with ID:', id);
      setConversationId(id);
      
      setActiveScenario(scenarioTitle);
      setActiveSubtitle(subtitle);
      setActiveDescription(description);
      setCurrentView('call');
      setTranscript('');
      setAnalysis('');
    } catch (error) {
      console.error('Failed to start conversation:', error);
      setStatus('Failed to start conversation');
    }
  };

  const endCall = async () => {
    try {
      console.log('endCall initiated - handling analysis...');
      setStatus('Ending call...');
      // First update the transcript one final time
      const finalTranscript = transcript;
      console.log('Current transcript:', finalTranscript);
      
      // End the session
      await conversation.endSession();
      
      // Wait a bit for any final updates
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Show results view
      setCurrentView('results');
      
      // Generate analysis using the final transcript
      console.log('Sending analysis request with transcript');
      
      // Use the langfuse_gpt4o_endpoint instead, which accepts transcript directly
      const response = await fetch(`${SCHOOLJOY_API_URL}/langfuse_gpt4o_endpoint`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt_name: "generate_call_analysis_health_faith_medicine",
          prompt_inputs: {
            transcript: finalTranscript
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate analysis');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        setAnalysis(prev => prev + chunk);
      }
    } catch (error) {
      console.error('Error ending call:', error);
      setStatus('Error ending call');
      setAnalysis('Error: Failed to generate analysis');
    }
  };

  const handleSendEmail = async (email) => {
    try {
      setEmailStatus('Sending...');
      
      // For ElevenLabs Demo, always send as Nimo email since there's no callId/promptName
      const emailPayload = {
        email,
        transcript,
        analysis,
        simulation_title: processContent(activeScenario).text || 'ElevenLabs Demo', // Use processed scenario title
        email_type: 'nimo'
      };

      const response = await fetch(`${SCHOOLJOY_API_URL}/send_email_interview/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailPayload),
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      setEmailStatus('Email sent successfully!');
      setShowEmailForm(false);
      
      setTimeout(() => {
        setEmailStatus(null);
      }, 3000);
      
    } catch (error) {
      console.error('Error sending email:', error);
      setEmailStatus('Failed to send email. Please try again.');
      
      setTimeout(() => {
        setEmailStatus(null);
      }, 3000);
    }
  };

  const handleStartNewScenario = () => {
    setActiveScenario(null);
    setActiveSubtitle(null);
    setActiveDescription(null);
    setTranscript('');
    setAnalysis('');
    setConversationId(null);
    setShowEmailForm(false);
    setEmailStatus(null);
    setStatus('Ready to start');
    setCurrentView('selection');
  };

  return (
    <div className="min-h-screen bg-background-gray text-text-dark flex flex-col font-inter">
      <div className="fixed inset-0 bg-gradient-to-b from-background-gray to-primary-blue opacity-20 -z-10"></div>
      
      <div className="flex-grow flex flex-col items-center justify-start p-4 sm:p-10 pb-20 overflow-y-auto">
        <motion.h1
          className="text-3xl sm:text-4xl lg:text-5xl font-poppins font-bold mb-6 sm:mb-8 text-center leading-tight bg-clip-text text-transparent bg-gradient-to-r from-primary-blue to-secondary-blue-1"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 1 }}
        >
          ElevenLabs Demo
        </motion.h1>

        {currentView === 'selection' && (
          <motion.div
            key="scenario-selection"
            className="w-full max-w-6xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <motion.p
              className="text-lg sm:text-xl mb-10 sm:mb-16 text-center text-text-dark max-w-4xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 1 }}
            >
              Choose from our diverse range of AI-powered conversation experiences designed to help you learn, practice, and grow in different areas.
            </motion.p>
            
            {/* Grid container for scenarios */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {scenarios.map((scenario, index) => (
                <motion.div
                  key={scenario.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <ScenarioCard
                    title={scenario.title}
                    subtitle={scenario.subtitle}
                    description={scenario.description}
                    onLaunch={() => startCall(
                      scenario.title,
                      scenario.agentId,
                      scenario.subtitle,
                      scenario.description
                    )}
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {currentView === 'call' && (
          <motion.div
            key="active-call"
            className="flex-grow flex items-center justify-center w-full py-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <ActiveCall
              scenarioTitle={activeScenario}
              subtitle={activeSubtitle}
              description={activeDescription}
              status={status}
              onEndCall={endCall}
              transcript={transcript}
            />
          </motion.div>
        )}

        {currentView === 'results' && (
          <motion.div
            key="call-results"
            className="w-full max-w-6xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="mt-6 sm:mt-10 p-4 sm:p-8 w-full bg-white">
              <div className="mb-6 sm:mb-8 text-center">
                {processContent(activeScenario).imageUrl && (
                  <img 
                    src={processContent(activeScenario).imageUrl} 
                    alt="" 
                    className="w-full h-auto mb-4 rounded-lg object-cover max-h-[200px] mx-auto" 
                  />
                )}
                <h2 className="text-xl sm:text-h2 font-poppins font-bold text-primary-blue">
                  {processContent(activeScenario).text || 'Call Results'}
                </h2>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:space-x-6">
                <TranscriptSection transcript={transcript} />
                <AnalysisSection analysis={analysis} />
              </div>
              
              <motion.div 
                className="mt-6 sm:mt-10 flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <Button
                  onClick={handleStartNewScenario}
                  variant="primary"
                  className="px-6 py-3 sm:px-8 sm:py-4 text-base sm:text-lg"
                >
                  Try Another Conversation
                </Button>
                <Button
                  onClick={() => setShowEmailForm(true)}
                  variant="secondary"
                  className="px-6 py-3 sm:px-8 sm:py-4 text-base sm:text-lg flex items-center justify-center"
                >
                  <Mail className="mr-2" size={20} />
                  Send Email
                </Button>
              </motion.div>

              {emailStatus && (
                <motion.p
                  className="mt-4 text-center text-lg text-gray-700"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {emailStatus}
                </motion.p>
              )}

              <AnimatePresence>
                {showEmailForm && (
                  <EmailForm
                    key="email-form"
                    onSendEmail={handleSendEmail}
                    onCancel={() => setShowEmailForm(false)}
                  />
                )}
              </AnimatePresence>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ElevenLabsDemo;
